import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { CLIENT_ID } from '../../lib/client-context';
import { moodboardKeys } from './moodboardKeys';
import { 
  MoodboardItemData,
  FormattedMoodboardItem,
  CreateMoodboardItemInput,
  UpdateMoodboardItemInput,
  formatMoodboardItem
} from './moodboardTypes';
import { db } from '../../lib/offline-db';
import { queueOfflineOperation } from '../../lib/offline-sync';
import { getConnectionState } from '../../contexts/ConnectionStateContext';
import { useQueryErrorHandler } from '../useQueryErrorHandler';
import { invalidateQuery, safeUpdateQueryData } from '../../utils/queryInvalidationUtils';

/**
 * Hook for fetching all moodboard items for a specific topic
 * Supports offline mode by using IndexedDB when offline
 */
export function useMoodboardItems(topicId: string | null) {
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: topicId ? moodboardKeys.byTopic(topicId) : moodboardKeys.lists(),
    queryFn: async () => {
      if (!topicId) {
        throw new Error('Topic ID is required');
      }

      const fetchId = Date.now().toString().slice(-4);
      logger.fetch(`Starting moodboard items fetch #${fetchId} for topic ${topicId} (online: ${isOnline})`);
      const startTime = performance.now();

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching moodboard items from IndexedDB #${fetchId}`);
          
          try {
            // Get moodboard items from IndexedDB
            const items = await db.moodboardItems
              .where('topic_id')
              .equals(topicId)
              .and(item => !item.isDeleted) // Filter out deleted items
              .toArray();
              
            logger.fetch(`Offline mode: loaded ${items.length} moodboard items from IndexedDB`);
            
            // Sort items by creation date (newest first)
            const sortedItems = [...items].sort((a, b) => 
              new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            );
            
            // Format items for UI
            return sortedItems.map(item => formatMoodboardItem(item as MoodboardItemData));
          } catch (offlineError) {
            logger.error(`Error fetching offline moodboard items #${fetchId}:`, offlineError);
            throw new Error('Failed to load moodboard items from offline storage');
          }
        }

        // Online flow - fetch from server
        const { data, error } = await supabase
          .from('moodboard_items')
          .select('*')
          .eq('topic_id', topicId)
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        // Store in IndexedDB for offline access
        if (data && data.length > 0) {
          try {
            await db.moodboardItems.bulkPut(data);
            logger.fetch(`Stored ${data.length} moodboard items in IndexedDB`);
          } catch (storageError) {
            logger.error(`Error storing moodboard items in IndexedDB:`, storageError);
            // Continue even if storage fails - this is non-critical
          }
        }

        // Format items using the helper function
        const formattedItems = (data || []).map(item => 
          formatMoodboardItem(item as MoodboardItemData)
        );

        // Log performance metrics
        const endTime = performance.now();
        const duration = endTime - startTime;
        logger.fetch(`Moodboard items fetch #${fetchId} completed in ${duration.toFixed(2)}ms`);

        return formattedItems;
      } catch (error) {
        // Use the error handler to handle and log the error
        const appError = handleError(error, {
          action: 'toast',
          context: { 
            fetchId, 
            operation: 'fetchMoodboardItems',
            topicId,
            isOnline 
          }
        });
        
        throw appError;
      }
    },
    enabled: !!topicId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for fetching a single moodboard item by ID
 * Supports offline mode by using IndexedDB when offline
 */
export function useMoodboardItem(itemId: string | null) {
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: itemId ? moodboardKeys.detail(itemId) : moodboardKeys.details(),
    queryFn: async () => {
      if (!itemId) {
        throw new Error('Moodboard item ID is required');
      }

      logger.fetch(`Fetching moodboard item ${itemId} (online: ${isOnline})`);

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching moodboard item ${itemId} from IndexedDB`);
          
          try {
            // Get moodboard item from IndexedDB
            const item = await db.moodboardItems.get(itemId);
            
            if (!item || item.isDeleted) {
              throw new Error(`Moodboard item ${itemId} not found in offline storage`);
            }
            
            // Format the item
            return formatMoodboardItem(item as MoodboardItemData);
          } catch (offlineError) {
            logger.error(`Error fetching offline moodboard item ${itemId}:`, offlineError);
            throw new Error('Failed to load moodboard item from offline storage');
          }
        }

        // Online flow - fetch from server
        const { data, error } = await supabase
          .from('moodboard_items')
          .select('*')
          .eq('id', itemId)
          .single();

        if (error) throw error;
        
        // Store in IndexedDB for offline access
        try {
          await db.moodboardItems.put(data);
        } catch (storageError) {
          logger.error(`Error storing moodboard item in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Format and return the item
        return formatMoodboardItem(data as MoodboardItemData);
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'fetchMoodboardItem', itemId, isOnline }
        });
        throw appError;
      }
    },
    enabled: !!itemId,
  });
}

/**
 * Hook for adding moodboard items
 * Supports offline mode by storing in IndexedDB and queueing for sync
 */
export function useAddMoodboardItems() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async (inputs: { 
      topicId: string, 
      items: Array<Omit<CreateMoodboardItemInput, 'topicId'>> 
    }): Promise<FormattedMoodboardItem[]> => {
      const { topicId, items } = inputs;
      
      logger.store('Adding new moodboard items', {
        topicId,
        itemCount: items.length,
        is_online: isOnline,
      });
      
      try {
        const supabaseClient = await supabase;
        const { data: { user } } = await supabaseClient.auth.getUser();
        
        if (!user) {
          throw new Error('No authenticated user');
        }
        
        // Create item data objects with metadata
        const now = new Date().toISOString();
        const itemsWithMetadata: MoodboardItemData[] = items.map(item => ({
          id: crypto.randomUUID(),
          topic_id: topicId,
          user_id: user.id,
          url: item.url,
          width: item.width,
          height: item.height,
          created_at: now,
          updated_at: now,
          metadata: {
            client_id: CLIENT_ID,
            last_update: now
          }
        }));
        
        // If offline, save locally and queue for sync
        if (!isOnline) {
          // Store in IndexedDB
          await db.moodboardItems.bulkPut(itemsWithMetadata);
          
          // Queue for sync when online
          for (const item of itemsWithMetadata) {
            await queueOfflineOperation('moodboard_items', 'create', item.id, item);
          }
          
          // Return formatted items
          return itemsWithMetadata.map(item => formatMoodboardItem(item));
        }
        
        // Online flow - send to server
        const { data: newItems, error } = await supabaseClient
          .from('moodboard_items')
          .insert(itemsWithMetadata)
          .select();
          
        if (error) throw error;
        
        // Store in IndexedDB for offline access
        try {
          await db.moodboardItems.bulkPut(newItems);
          logger.fetch(`Stored ${newItems.length} moodboard items in IndexedDB`);
        } catch (storageError) {
          logger.error(`Error storing moodboard items in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }
        
        // Return formatted items
        return (newItems || []).map(item => formatMoodboardItem(item as MoodboardItemData));
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'createMoodboardItems', topicId }
        });
        throw appError;
      }
    },
    
    onSuccess: (newItems, variables) => {
      const { topicId } = variables;
      
      // Invalidate moodboard items list for the topic
      invalidateQuery(queryClient, moodboardKeys.byTopic(topicId), {
        refetchType: 'active',
        context: { operation: 'createMoodboardItems', topicId }
      });
      
      // Add the new items to their detail caches
      for (const item of newItems) {
        safeUpdateQueryData(queryClient, moodboardKeys.detail(item.id), () => item, {
          context: { operation: 'createMoodboardItem', itemId: item.id }
        });
      }
    },
  });
}

/**
 * Hook for updating a moodboard item
 * Supports offline mode by updating IndexedDB and queueing for sync
 */
export function useUpdateMoodboardItem() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({
      topicId,
      itemId,
      updates
    }: {
      topicId: string;
      itemId: string;
      updates: UpdateMoodboardItemInput;
    }): Promise<FormattedMoodboardItem> => {
      if (!itemId) {
        throw new Error('Moodboard item ID is required');
      }

      const now = new Date().toISOString();
      
      // Create update payload with metadata
      const updatePayload: Partial<MoodboardItemData> = {
        ...updates,
        updated_at: now,
        metadata: {
          client_id: CLIENT_ID,
          last_update: now
        }
      };
      
      logger.store('Updating moodboard item', {
        itemId,
        topicId,
        is_online: isOnline,
      });
      
      // If offline, update locally and queue for sync
      if (!isOnline) {
        try {
          // Get current item from IndexedDB
          const currentItem = await db.moodboardItems.get(itemId);
          
          if (!currentItem || currentItem.isDeleted) {
            throw new Error(`Moodboard item ${itemId} not found in offline storage`);
          }
          
          // Merge updates with current data
          const updatedItem = {
            ...currentItem,
            ...updatePayload
          };
          
          // Update in IndexedDB
          await db.moodboardItems.put(updatedItem);
          
          // Queue for sync when online
          await queueOfflineOperation('moodboard_items', 'update', itemId, updatedItem);
          
          // Return formatted item
          return formatMoodboardItem(updatedItem as MoodboardItemData);
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'updateMoodboardItem', itemId, mode: 'offline' }
          });
          throw error;
        }
      }

      // Online flow - update on server
      try {
        // Get current item to access its metadata
        const { data: currentItem, error: fetchError } = await supabase
          .from('moodboard_items')
          .select('metadata')
          .eq('id', itemId)
          .single();
          
        if (fetchError) throw fetchError;
        
        // Preserve existing metadata fields
        if (currentItem?.metadata) {
          updatePayload.metadata = {
            ...currentItem.metadata,
            ...updatePayload.metadata
          };
        }
        
        // Update the item
        const { data, error } = await supabase
          .from('moodboard_items')
          .update(updatePayload)
          .eq('id', itemId)
          .select()
          .single();

        if (error) throw error;
        
        // Update in IndexedDB for offline access
        try {
          // Get current data first to preserve fields not included in the update
          const existingItem = await db.moodboardItems.get(itemId);
          
          if (existingItem) {
            await db.moodboardItems.put({
              ...existingItem,
              ...data
            });
            logger.fetch(`Updated moodboard item ${itemId} in IndexedDB`);
          }
        } catch (storageError) {
          logger.error(`Error updating moodboard item in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }
        
        // Format and return the item
        return formatMoodboardItem(data as MoodboardItemData);
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'updateMoodboardItem', itemId }
        });
        throw appError;
      }
    },
    
    onSuccess: (updatedItem, variables) => {
      const { topicId, itemId } = variables;
      
      // Update item in cache
      safeUpdateQueryData(queryClient, moodboardKeys.detail(itemId), () => updatedItem, {
        context: { operation: 'updateMoodboardItem', itemId }
      });
      
      // Update item in topic list
      safeUpdateQueryData<FormattedMoodboardItem[]>(
        queryClient,
        moodboardKeys.byTopic(topicId),
        (items) => {
          if (!items) return undefined;
          
          return items.map(item => item.id === itemId ? updatedItem : item);
        },
        { context: { operation: 'updateMoodboardItem', itemId } }
      );
    },
  });
}

/**
 * Hook for deleting a moodboard item
 * Supports offline mode by marking as deleted in IndexedDB and queueing for sync
 */
export function useDeleteMoodboardItem() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({
      topicId,
      itemId
    }: {
      topicId: string;
      itemId: string;
    }) => {
      try {
        logger.store('Deleting moodboard item', {
          itemId,
          topicId,
          is_online: isOnline,
        });
        
        // If offline, mark as deleted locally and queue for sync
        if (!isOnline) {
          logger.store('Offline mode: Marking moodboard item as deleted locally', { itemId });
          
          try {
            // Mark as deleted in IndexedDB
            await db.moodboardItems.update(itemId, { isDeleted: true });
            
            // Queue for sync when online
            await queueOfflineOperation('moodboard_items', 'delete', itemId, { id: itemId });
            
            return { itemId, topicId };
          } catch (offlineError) {
            const error = handleError(offlineError, {
              action: 'toast',
              context: { operation: 'deleteMoodboardItem', itemId, mode: 'offline' }
            });
            throw error;
          }
        }

        // Online flow - delete from server
        const { error } = await supabase.from('moodboard_items').delete().eq('id', itemId);

        if (error) throw error;

        // Remove from IndexedDB
        try {
          await db.moodboardItems.delete(itemId);
          logger.fetch(`Deleted moodboard item ${itemId} from IndexedDB`);
        } catch (storageError) {
          logger.error(`Error deleting moodboard item from IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        return { itemId, topicId };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'deleteMoodboardItem', itemId }
        });
        throw appError;
      }
    },
    
    onSuccess: (result) => {
      const { itemId, topicId } = result;
      
      // Remove item from cache
      queryClient.removeQueries({ 
        queryKey: moodboardKeys.detail(itemId),
        exact: true 
      });

      // Update the items list in the cache by filtering out the deleted item
      safeUpdateQueryData<FormattedMoodboardItem[]>(
        queryClient,
        moodboardKeys.byTopic(topicId),
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.filter(item => item.id !== itemId);
        },
        { context: { operation: 'deleteMoodboardItem', itemId, topicId } }
      );
    },
  });
}

/**
 * Hook for deleting multiple moodboard items
 */
export function useDeleteMoodboardItems() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({
      topicId,
      itemIds
    }: {
      topicId: string;
      itemIds: string[];
    }) => {
      try {
        logger.store('Deleting multiple moodboard items', {
          itemIds,
          count: itemIds.length,
          topicId,
          is_online: isOnline,
        });
        
        // If offline, mark as deleted locally and queue for sync
        if (!isOnline) {
          logger.store('Offline mode: Marking multiple moodboard items as deleted locally', { 
            count: itemIds.length 
          });
          
          try {
            // Process each item
            for (const itemId of itemIds) {
              // Mark as deleted in IndexedDB
              await db.moodboardItems.update(itemId, { isDeleted: true });
              
              // Queue for sync when online
              await queueOfflineOperation('moodboard_items', 'delete', itemId, { id: itemId });
            }
            
            return { itemIds, topicId };
          } catch (offlineError) {
            const error = handleError(offlineError, {
              action: 'toast',
              context: { 
                operation: 'deleteMoodboardItems', 
                count: itemIds.length, 
                mode: 'offline' 
              }
            });
            throw error;
          }
        }

        // Online flow - delete from server
        const { error } = await supabase.from('moodboard_items').delete().in('id', itemIds);

        if (error) throw error;

        // Remove from IndexedDB
        try {
          await Promise.all(itemIds.map(id => db.moodboardItems.delete(id)));
          logger.fetch(`Deleted ${itemIds.length} moodboard items from IndexedDB`);
        } catch (storageError) {
          logger.error(`Error deleting moodboard items from IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        return { itemIds, topicId };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { 
            operation: 'deleteMoodboardItems', 
            count: itemIds.length 
          }
        });
        throw appError;
      }
    },
    
    onSuccess: (result) => {
      const { itemIds, topicId } = result;
      
      // Remove items from cache
      for (const itemId of itemIds) {
        queryClient.removeQueries({ 
          queryKey: moodboardKeys.detail(itemId),
          exact: true 
        });
      }

      // Update the items list in the cache by filtering out the deleted items
      safeUpdateQueryData<FormattedMoodboardItem[]>(
        queryClient,
        moodboardKeys.byTopic(topicId),
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.filter(item => !itemIds.includes(item.id));
        },
        { context: { operation: 'deleteMoodboardItems', count: itemIds.length, topicId } }
      );
    },
  });
}